import type { MedplumClient } from "@medplum/core";
import type { Invoice, PaymentReconciliation } from "@medplum/fhirtypes";

import chunk from "lodash/chunk";
import uniqBy from "lodash/uniqBy";

import { LEDGER_IDENTIFIER_CHUNK_SIZE } from "@/billing/constants/ledger-search";
import { practiceOrganizationIdFrom } from "@/billing/utils/invoice-provenance";
import { ORGANIZATION_ID } from "@/constants/local-storage";
import { QuiltedHealthIdentifierSystems } from "@/fhir/enums/quilted-health";
import { processWithConcurrencyLimit } from "@/utils/concurrency";
import { identifierValues, joinReferenceString, joinTokenString } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

/** Concurrent PaymentReconciliation identifier searches. */
const LEDGER_PAYMENT_SEARCH_CONCURRENCY = 10;

interface CompartmentSearch {
  compartment: string;
  statementNumbers: string[];
}

/**
 * Loads the PaymentReconciliations for the given statements, each searched inside the practice
 * that owns its Invoice.
 *
 * A PaymentReconciliation has no patient subject, so it is found by statement number. Statement
 * numbers are only unique within a practice, so each search carries `_compartment` of the
 * Invoice's issuer: a payment filed under another practice with the same number never matches.
 * An Invoice with no Organization issuer is searched in the practice active in the browser
 * (`ORGANIZATION_ID` in localStorage, read the same way by `statement-document.ts`). Its payments
 * are found only when that practice owns them; when another practice is active the search
 * returns none for that Invoice.
 *
 * @param medplum The Medplum client.
 * @param invoices The statements whose payments to load.
 * @returns The matching reconciliations, deduplicated by id. Rejects if any search page fails,
 *   or if an Invoice with statement numbers has no Organization issuer and there is no active
 *   practice, so a partial list is never reported as complete.
 */
export async function searchLedgerPaymentReconciliations(
  medplum: MedplumClient,
  invoices: Invoice[],
): Promise<PaymentReconciliation[]> {
  const searches = groupStatementNumbersByCompartment(invoices).flatMap(
    ({ compartment, statementNumbers }) =>
      chunk(statementNumbers, LEDGER_IDENTIFIER_CHUNK_SIZE).map((statementNumberChunk) => ({
        _compartment: compartment,
        identifier: paymentReconciliationIdentifierQuery(statementNumberChunk),
      })),
  );
  if (searches.length === 0) return [];

  const pages = await processWithConcurrencyLimit(
    searches,
    async (searchParams) => {
      const result = await fetchAllPaginatedResources<PaymentReconciliation>(
        medplum,
        "PaymentReconciliation",
        searchParams,
        { throwOnPageError: true },
      );
      if (!result.success) throw result.error;
      return result.data;
    },
    LEDGER_PAYMENT_SEARCH_CONCURRENCY,
  );

  return uniqBy(pages.flat(), "id");
}

function groupStatementNumbersByCompartment(invoices: Invoice[]): CompartmentSearch[] {
  const activeOrganizationId = localStorage.getItem(ORGANIZATION_ID);
  const statementNumbersByCompartment = new Map<string, Set<string>>();

  for (const invoice of invoices) {
    const statementNumbers = identifierValues(
      invoice,
      QuiltedHealthIdentifierSystems.InvoiceIdentifier,
    );
    if (statementNumbers.length === 0) continue;

    const practiceId = practiceOrganizationIdFrom(invoice) ?? activeOrganizationId;
    if (!practiceId) {
      throw new Error("No active practice to scope the PaymentReconciliation search");
    }

    const compartment = joinReferenceString("Organization", practiceId);
    const compartmentStatementNumbers =
      statementNumbersByCompartment.get(compartment) ?? new Set<string>();
    for (const statementNumber of statementNumbers) {
      compartmentStatementNumbers.add(statementNumber);
    }
    statementNumbersByCompartment.set(compartment, compartmentStatementNumbers);
  }

  return [...statementNumbersByCompartment].map(([compartment, statementNumbers]) => ({
    compartment,
    statementNumbers: [...statementNumbers],
  }));
}

function paymentReconciliationIdentifierQuery(statementNumbers: string[]): string {
  return statementNumbers
    .reduce<string[]>((tokens, statementNumber) => {
      const token = joinTokenString(
        QuiltedHealthIdentifierSystems.InvoiceIdentifier,
        statementNumber,
      );
      if (token) tokens.push(token);
      return tokens;
    }, [])
    .join(",");
}
